import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type { IMarket } from '@/interface/IMatch';
import type { LeagueSize } from '@/interface/enum';
import { SportType } from '@/interface/enum';
import { remove, upsert } from '@/libraries/array';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { formatMarket } from '@/libraries/helpers/formatMatch';
import { useOddsToken } from '@/libraries/oddsApi/composables/useOddsToken';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';
import { useMarketsQuery, useMarketsSubscription, useMarketsV2Query, useMarketsV2Subscription } from '@/types';

export function useMarkets(
    matchId: number,
    isLive: Ref<boolean>,
    sportType: SportType,
    leagueSize: LeagueSize,
    leagueId: number,
    isEnabled: Ref<boolean>,
) {
    const markets = ref<IMarket[]>([]);
    const { isWsConnected } = storeToRefs(useRootStore());
    const { isDynamicMarketMTokenEnabled } = storeToRefs(useFeatureToggleStore());
    const { getOddsToken } = useOddsToken();

    function getPollingInterval() {
        if (isWsConnected.value) {
            return undefined;
        }

        return isLive.value ? 30000 : 60000;
    }

    const oldVariables = deepCompareComputed(() => ({
        query: {
            matchId,
            isLive: isLive.value,
        },
    }));

    const oldEnabled = computed(() => isEnabled.value && !isDynamicMarketMTokenEnabled.value);

    const oldQuery = useMarketsQuery(
        oldVariables,
        deepCompareComputed(() => ({
            enabled: oldEnabled.value,
            pollInterval: getPollingInterval(),
        })),
    );

    const oldSubscription = useMarketsSubscription(
        oldVariables,
        deepCompareComputed(() => ({
            enabled: oldEnabled.value,
        })),
    );

    const variables = deepCompareComputed(() => {
        const oddsToken = getOddsToken(sportType, leagueSize);

        return {
            query: {
                matchId,
                isLive: isLive.value,
                oddsToken,
                sportType: SportType.toOddsApi(sportType),
                leagueId,
            },
        };
    });

    const enabled = computed(() => isEnabled.value && isDynamicMarketMTokenEnabled.value && !!variables.value.query.oddsToken);

    const query = useMarketsV2Query(
        variables,
        deepCompareComputed(() => ({
            enabled: enabled.value,
            pollInterval: getPollingInterval(),
        })),
    );

    const subscription = useMarketsV2Subscription(
        variables,
        deepCompareComputed(() => ({
            enabled: enabled.value,
        })),
    );

    oldQuery.onResult((result) => {
        if (!result) return;
        markets.value = result.map(item => Object.freeze(formatMarket(item)));
    });
    query.onResult((result) => {
        if (!result) return;
        markets.value = result.map(item => Object.freeze(formatMarket(item)));
    });

    oldSubscription.onSubscription((result) => {
        if (!result) return;
        const updatedMarkets = result.updated.map(item => Object.freeze(formatMarket(item)));
        upsert(markets.value, updatedMarkets, market => market.id);
        remove(markets.value, result.deleted, (market, id) => market.id === id);
    });
    subscription.onSubscription((result) => {
        if (!result) return;
        const updatedMarkets = result.updated.map(item => Object.freeze(formatMarket(item)));
        upsert(markets.value, updatedMarkets, market => market.id);
        remove(markets.value, result.deleted, (market, id) => market.id === id);
    });

    const loaded = computed(() => (isDynamicMarketMTokenEnabled.value ? query.loaded.value : oldQuery.loaded.value));
    const loading = computed(() => (isDynamicMarketMTokenEnabled.value ? query.loading.value : oldQuery.loading.value));
    const refetch = () => (isDynamicMarketMTokenEnabled.value ? query.refetch() : oldQuery.refetch());

    return {
        markets,
        loaded,
        loading,
        refetch,
    };
}
